import { createSlice } from '@reduxjs/toolkit';
import {
  addServiceOrderLog,
  addServiceOrderSubtask,
  createServiceOrder,
  createServiceOrderStatus,
  deleteServiceOrderById,
  deleteServiceOrderStatus,
  deleteServiceOrderSubtask,
  getServiceOrdersDataByWorkspaceId,
  updateServiceOrderById,
  updateServiceOrderStatus,
  updateServiceOrderSubtask,
} from '../thunks/serviceOrders';

function objFromArray(array, key = 'id') {
  return array.reduce((accumulator, current) => {
    accumulator[current[key]] = current;
    return accumulator;
  }, {});
}

const initialState = {
  isLoading: true,
  error: null,
  board: {
    columns: [],
    tasks: {},
  },
};

const kanbanSlice = createSlice({
  name: 'kanban',
  initialState,
  reducers: {
    persistCard(state, action) {
      const tasks = action.payload;
      state.board.tasks = tasks;
    },
    persistColumn(state, action) {
      state.board.columns = action.payload;
    },
    moveTaskFromColumnToColumn(state, action) {
      const { source, destination, taskId } = action.payload;

      // Remove task from source column
      state.board.columns = state.board.columns.map((col) => {
        if (col.id === source) {
          return {
            ...col,
            taskIds: col.taskIds.filter((id) => id !== taskId),
          };
        }
        return col;
      });

      // Add task to destination column
      state.board.columns = state.board.columns.map((col) => {
        if (col.id === destination) {
          return {
            ...col,
            taskIds: [...col.taskIds, taskId],
          };
        }
        return col;
      });
    },
  },
  extraReducers: (builder) => {
    // GET BOARD
    builder
      .addCase(getServiceOrdersDataByWorkspaceId.fulfilled, (state, action) => {
        const { columns, tasks: tasksData } = action.payload;
        const tasksObj = objFromArray(tasksData);

        const tasks = columns.reduce((acc, curr) => {
          acc[curr.id] = curr.taskIds.map((id) => tasksObj[id]);
          return acc;
        }, {});

        state.isLoading = false;
        state.board = {
          columns,
          tasks,
        };

        return state;
      })
      .addCase(getServiceOrdersDataByWorkspaceId.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createServiceOrderStatus.fulfilled, (state, action) => {
        const newColumn = {
          ...action.payload,
        };

        state.board.columns = state.board.columns.concat(newColumn);
        state.board.tasks[newColumn.id] = [];
      })
      .addCase(updateServiceOrderStatus.fulfilled, (state, action) => {
        const column = action.payload;
        state.isLoading = false;
        state.board.columns = state.board.columns.map((col) =>
          col.id === column.id ? { ...column } : col
        );
      })
      .addCase(deleteServiceOrderStatus.fulfilled, (state, action) => {
        const columnId = action.payload.id;

        state.board.columns = state.board.columns.filter(
          (col) => col.id !== columnId
        );
        delete state.board.tasks[columnId];

        return state;
      })
      .addCase(createServiceOrder.fulfilled, (state, action) => {
        const newCard = action.payload;
        const columnId = action.meta.arg.columnId;

        if (!state.board.tasks[columnId]) return;

        state.board.tasks[columnId] =
          state.board.tasks[columnId].concat(newCard);
        state.board.columns = state.board.columns.map((col) =>
          col.id === columnId
            ? { ...col, taskIds: col.taskIds.concat(newCard.id) }
            : col
        );
      })
      .addCase(updateServiceOrderById.fulfilled, (state, action) => {
        const { columnId, ...newTaskData } = action.payload;
        if (!state.board.tasks[columnId]) return;
        state.board.tasks[columnId] = state.board.tasks[columnId].map((task) =>
          task.id === action.payload.id ? { ...task, ...newTaskData } : task
        );
      })
      .addCase(deleteServiceOrderById.fulfilled, (state, action) => {
        const cardId = action.payload.serviceOrderId;
        const columnId = action.payload.serviceOrderStatusId;

        state.board.columns = state.board.columns.map((col) =>
          col.id === columnId
            ? {
                ...col,
                taskIds: (col.taskIds || []).filter((id) => id !== cardId),
              }
            : col
        );

        if (state.board.tasks[columnId]) {
          state.board.tasks[columnId] = state.board.tasks[columnId].filter(
            (task) => task.id !== cardId
          );
        }
      })
      .addCase(addServiceOrderLog.fulfilled, (state, action) => {
        const newComment = action.payload;
        const column = state.board.columns.find((col) =>
          (col.taskIds || []).includes(newComment.serviceOrderId)
        );

        if (!column || !state.board.tasks[column.id]) return;

        state.board.tasks[column.id] = state.board.tasks[column.id].map(
          (task) =>
            task.id === newComment.serviceOrderId
              ? {
                  ...task,
                  comments: (task.comments || []).concat(newComment),
                }
              : task
        );
      })
      .addCase(addServiceOrderSubtask.fulfilled, (state, action) => {
        const newSubtask = action.payload;
        const column = state.board.columns.find((col) =>
          (col.taskIds || []).includes(newSubtask.serviceOrderId)
        );

        if (!column || !state.board.tasks[column.id]) return;

        state.board.tasks[column.id] = state.board.tasks[column.id].map(
          (task) =>
            task.id === newSubtask.serviceOrderId
              ? {
                  ...task,
                  serviceOrderTasks: (task.serviceOrderTasks || []).concat(
                    newSubtask
                  ),
                }
              : task
        );
      })
      .addCase(updateServiceOrderSubtask.fulfilled, (state, action) => {
        const newSubtask = action.payload;
        const column = state.board.columns.find((col) =>
          (col.taskIds || []).includes(newSubtask.serviceOrderId)
        );

        if (!column || !state.board.tasks[column.id]) return;

        state.board.tasks[column.id] = state.board.tasks[column.id].map(
          (task) =>
            task.id === newSubtask.serviceOrderId
              ? {
                  ...task,
                  serviceOrderTasks: (task.serviceOrderTasks || []).reduce(
                    (acc, subtask) =>
                      subtask.id === newSubtask.id
                        ? acc.concat(newSubtask)
                        : acc.concat(subtask),
                    []
                  ),
                }
              : task
        );
      })
      .addCase(deleteServiceOrderSubtask.fulfilled, (state, action) => {
        const subtaskId = action.payload.id;
        const column = state.board.columns.find((col) =>
          (col.taskIds || []).includes(action.payload.serviceOrderId)
        );

        if (!column || !state.board.tasks[column.id]) return;

        state.board.tasks[column.id] = state.board.tasks[column.id].map(
          (task) =>
            task.id === action.payload.serviceOrderId
              ? {
                  ...task,
                  serviceOrderTasks: (task.serviceOrderTasks || []).filter(
                    (subtask) => subtask.id !== subtaskId
                  ),
                }
              : task
        );
      });
  },
});

// actions
export const { persistCard, persistColumn, moveTaskFromColumnToColumn } =
  kanbanSlice.actions;

// Reducer
export default kanbanSlice.reducer;
